'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useScreenDetection } from '@/utils/hooks/useScreenDetection';
import { store } from '@/store/store';
import { initializePollingOnLoad } from '@store/middleware/supplierSearchPollingMiddleware';
import { events, initPlugins } from '@Events';
import { captureFirstTouchAttribution } from '@/auth/attribution';
import { getService, registerSessionEndOnUnload, trackPageViewedInSession } from '@/events/core/enricher';

export const SetupHooks: React.FC = () => {
    // NOTE: intentionally `usePathname()` only, not `useSearchParams()` —
    // this component is mounted at the root layout via ClientWrapper, and
    // `useSearchParams()` there would opt the entire app out of static
    // rendering unless wrapped in its own <Suspense>. Query string (if
    // present) is read from `window.location.search` inside the effect
    // instead, which is client-only and carries no such risk.
    const pathname = usePathname();

    useEffect(() => {
        // Initialize event plugins (gtag, sentry, etc.)
        initPlugins();

        // Initialize polling for any active searches on mount
        initializePollingOnLoad(store);

        // Capture UTM attribution from URL params on first visit
        captureFirstTouchAttribution();

        // WE-115: best-effort session_end on tab close/navigation away.
        return registerSessionEndOnUnload();
    }, []);

    useEffect(() => {
        // WE-115: page_view on every SPA navigation, including the first
        // load — no destination (GTM/GA4/Meta/Mixpanel) auto-collects this
        // for a Next.js App Router client-side navigation.
        trackPageViewedInSession();
        const query = window.location.search;
        events.emit('page_view', {
            path: query ? `${pathname}${query}` : pathname,
            referrer: document.referrer || '',
            service: getService(),
        });
    }, [pathname]);

    useScreenDetection();
    return null;
};
